// Builds the initial MatchPhone document we PUT into the user's CouchDB on
// "New Match". Mirrors phone/src/services/matchService.ts `createDefaultMatch()`
// — same defaults, plus our home/away team names and a two-period timing
// (Grassroots Live's display logic only renders matches with periodsNo = "2").

import { IntegrationPartner } from "refsix-js-models";
import { HOME_TEAM, AWAY_TEAM, SINGLE_HALF_LENGTH_MS } from "./defaults";

const HALF_LENGTH_MINUTES = SINGLE_HALF_LENGTH_MS / 60_000;
const HALF_TIME_LENGTH_MINUTES = 15;

// Replicates the GUID format used by the phone app (matchService.ts:730). Not
// a true v4 UUID — just 8-4-4-4-12 hex — but it matches the document `_id`
// shape the rest of the system has been writing for years.
export function newMatchId(): string {
  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return (
    s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4()
  );
}

// The MatchPhone document the live API will read and append events to.
// Field set follows `models/src/models/matchPhone.ts` and
// phone/src/services/matchService.ts `createDefaultMatch()`.
//
// `externalMatchId`, if provided non-empty, populates `integration.matchId`
// under the KNVB partner so downstream integrations can correlate this match
// with a record in the partner system. When omitted (or empty after trim),
// `integration` is left off the document entirely.
//
// `misconductCodeId` overrides the default "fifa" code list used for cards.
// Random matches pass "netherlands" so their incident events' `reason`
// fields are picked from the Netherlands codes list.
export function buildDefaultMatchPhone(
  matchId: string,
  nowIso: string,
  externalMatchId?: string,
  misconductCodeId: string = "fifa"
) {
  const trimmedExternalId = externalMatchId?.trim();
  const integration = trimmedExternalId
    ? { partner: IntegrationPartner.knvb, matchId: trimmedExternalId }
    : undefined;

  return {
    _id: matchId,
    date: nowIso,
    officialRole: "referee",
    competition: "",
    venue: "",
    homeTeam: HOME_TEAM.teamName,
    homeTeamShort: HOME_TEAM.shortName,
    homeColor: HOME_TEAM.color,
    awayTeam: AWAY_TEAM.teamName,
    awayTeamShort: AWAY_TEAM.shortName,
    awayColor: AWAY_TEAM.color,
    periodsNo: "2",
    timings: {
      period1: HALF_LENGTH_MINUTES,
      period2: HALF_LENGTH_MINUTES,
      interval1: HALF_TIME_LENGTH_MINUTES,
    },
    teamSize: 11,
    subsNo: 1,
    subOpportunities: false,
    extraTimeAvailable: false,
    penaltiesAvailable: false,
    withGoalScorers: false,
    sinBinSystem: "none",
    misconductCodeId,
    refsixCount: 0,
    refsixUpdateCount: 0,
    matchFinished: false,
    matchInProgress: false,
    eventsProcessed: false,
    matchEvents: {},
    results: {},
    source: "live-api-tester",
    players: {
      [HOME_TEAM.teamName]: HOME_TEAM.players,
      [AWAY_TEAM.teamName]: AWAY_TEAM.players,
    },
    ...(integration ? { integration } : {}),
  } as const;
}
